<template>
    <Teleport to="body">
        <Transition name="version-panel">
            <div v-if="isVisible" class="versionPanel" @click.self="close">
                <div class="versionPanel__card">
                    <div class="versionPanel__header">
                        <div class="versionPanel__title">Canvas Version</div>
                        <button class="versionPanel__close" @click="close">×</button>
                    </div>
                    <div class="versionPanel__content">
                        <div class="versionPanel__row">
                            <span class="versionPanel__label">Version</span>
                            <span class="versionPanel__value">{{ versionInfo.version }}</span>
                        </div>
                        <div class="versionPanel__row">
                            <span class="versionPanel__label">Branch</span>
                            <span class="versionPanel__value versionPanel__value--mono">{{
                                versionInfo.branch
                            }}</span>
                        </div>
                        <div class="versionPanel__row">
                            <span class="versionPanel__label">Commit</span>
                            <span class="versionPanel__value versionPanel__value--mono">{{
                                versionInfo.commitShortHash
                            }}</span>
                        </div>
                        <div class="versionPanel__row">
                            <span class="versionPanel__label">Commit Date</span>
                            <span class="versionPanel__value">{{ versionInfo.commitDate }}</span>
                        </div>
                        <div class="versionPanel__row">
                            <span class="versionPanel__label">Build Date</span>
                            <span class="versionPanel__value">{{ versionInfo.buildDate }}</span>
                        </div>
                        <div class="versionPanel__row">
                            <span class="versionPanel__label">Environment</span>
                            <span
                                class="versionPanel__value versionPanel__value--env"
                                :data-env="versionInfo.environment"
                            >
                                {{ versionInfo.environment }}
                            </span>
                        </div>
                    </div>
                    <div class="versionPanel__footer">
                        <button class="versionPanel__copy" @click="copyToClipboard">
                            {{ copyLabel }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
    import { computed, onMounted, onUnmounted, ref, Teleport, Transition } from 'vue';

    import { useVersionInfo } from '~/composables/useVersionInfo';

    const props = defineProps({
        modelValue: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits(['update:modelValue']);

    const { versionInfo } = useVersionInfo();
    const copyLabel = ref('Copy Info');

    const isVisible = computed({
        get: () => props.modelValue,
        set: (value) => emit('update:modelValue', value),
    });

    const close = () => {
        isVisible.value = false;
    };

    const copyToClipboard = async () => {
        const info = versionInfo.value;
        const text = [
            `Version: ${info.version}`,
            `Branch: ${info.branch}`,
            `Commit: ${info.commitShortHash}`,
            `Commit Date: ${info.commitDate}`,
            `Build Date: ${info.buildDate}`,
            `Environment: ${info.environment}`,
        ].join('\n');

        try {
            await navigator.clipboard.writeText(text);
            copyLabel.value = 'Copied!';
            setTimeout(() => {
                copyLabel.value = 'Copy Info';
            }, 2000);
        } catch {
            console.error('Failed to copy version info');
        }
    };

    // Keyboard shortcut: Escape to close
    const handleKeydown = (e) => {
        if (e.key === 'Escape' && isVisible.value) {
            close();
        }
    };

    onMounted(() => {
        window.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
        window.removeEventListener('keydown', handleKeydown);
    });
</script>

<style lang="scss" scoped>
    .versionPanel {
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);

        &__card {
            background: linear-gradient(145deg, rgba(20, 20, 25, 0.95), rgba(30, 30, 35, 0.95));
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 12px;
            box-shadow:
                0 0 30px rgba(255, 215, 0, 0.15),
                0 20px 40px rgba(0, 0, 0, 0.4);
            min-width: 320px;
            max-width: 90vw;
            overflow: hidden;
        }

        &__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px;
            border-bottom: 1px solid rgba(255, 215, 0, 0.2);
            background: rgba(255, 215, 0, 0.05);
        }

        &__title {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 18px;
            font-weight: 600;
            color: #ffd700;
            text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
        }

        &__close {
            width: 28px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: 6px;
            color: rgba(255, 255, 255, 0.7);
            font-size: 20px;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background: rgba(255, 255, 255, 0.2);
                color: #fff;
            }
        }

        &__content {
            padding: 16px 20px;
        }

        &__row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);

            &:last-child {
                border-bottom: none;
            }
        }

        &__label {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 13px;
            color: rgba(255, 255, 255, 0.5);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        &__value {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            color: #fff;
            font-weight: 500;

            &--mono {
                font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
                background: rgba(255, 255, 255, 0.08);
                padding: 4px 8px;
                border-radius: 4px;
            }

            &--env {
                padding: 4px 10px;
                border-radius: 4px;
                font-weight: 600;
                text-transform: uppercase;
                font-size: 11px;
                letter-spacing: 0.5px;

                &[data-env='prod'] {
                    background: rgba(239, 68, 68, 0.2);
                    color: #ef4444;
                }

                &[data-env='dev'] {
                    background: rgba(34, 197, 94, 0.2);
                    color: #22c55e;
                }

                &[data-env='staging'] {
                    background: rgba(234, 179, 8, 0.2);
                    color: #eab308;
                }
            }
        }

        &__footer {
            padding: 12px 20px;
            border-top: 1px solid rgba(255, 255, 255, 0.05);
            background: rgba(0, 0, 0, 0.2);
        }

        &__copy {
            width: 100%;
            padding: 10px 16px;
            background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 215, 0, 0.05));
            border: 1px solid rgba(255, 215, 0, 0.3);
            border-radius: 6px;
            color: #ffd700;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background: linear-gradient(
                    135deg,
                    rgba(255, 215, 0, 0.25),
                    rgba(255, 215, 0, 0.1)
                );
                box-shadow: 0 0 15px rgba(255, 215, 0, 0.2);
            }
        }
    }

    // Transition animations
    .version-panel-enter-active,
    .version-panel-leave-active {
        transition: all 0.3s ease;
    }

    .version-panel-enter-active .versionPanel__card,
    .version-panel-leave-active .versionPanel__card {
        transition: all 0.3s ease;
    }

    .version-panel-enter-from,
    .version-panel-leave-to {
        opacity: 0;
    }

    .version-panel-enter-from .versionPanel__card,
    .version-panel-leave-to .versionPanel__card {
        transform: scale(0.9) translateY(20px);
        opacity: 0;
    }
</style>
